// react
import React, { useState, useEffect } from 'react';

// third-party
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

// application
import PageHeader from '../shared/PageHeader';
import theme from '../../data/theme';

export default function ShopPageOrderSuccess() {
    const [orderInfo, setInfo] = useState(null);
    const [copiedField, setCopiedField] = useState(null);

    useEffect(() => {
        try {
            const stored = localStorage.getItem('CO-INFO');
            if (stored) {
                setInfo(JSON.parse(stored));
            }
        } catch (e) {
            console.error('Failed to parse order info from storage:', e);
        }
    }, []);

    const formatRupiah = (money) => {
        const num = parseFloat(money) || 0;
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
        }).format(num);
    };

    const handleCopy = (text, fieldName, label) => {
        if (!text) return;

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(String(text));
        } else {
            const el = document.createElement('textarea');
            el.value = String(text);
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
        }

        setCopiedField(fieldName);
        toast.success(`${label} berhasil disalin!`);
        setTimeout(() => {
            setCopiedField(null);
        }, 2200);
    };

    const getBankBadge = (bName = '') => {
        const upper = bName.toUpperCase();
        if (upper.includes('BCA')) return { bg: '#005baa', color: '#ffffff', label: 'BCA' };
        if (upper.includes('MANDIRI')) return { bg: '#002f6c', color: '#ffffff', label: 'MANDIRI' };
        if (upper.includes('BRI')) return { bg: '#00529c', color: '#ffffff', label: 'BRI' };
        if (upper.includes('JATIM')) return { bg: '#c91f28', color: '#ffffff', label: 'BANK JATIM' };
        if (upper.includes('BNI')) return { bg: '#f15a24', color: '#ffffff', label: 'BNI' };
        return { bg: '#002b66', color: '#ffffff', label: bName || 'BANK' };
    };

    const transactionCode = orderInfo?.transaction_code || '-';
    const transactionDate = orderInfo?.transaction_date || new Date().toISOString().split('T')[0];
    const transactionTotal = orderInfo?.transaction_total || 0;
    const bankName = (orderInfo?.bank_name || 'Bank Transfer').toUpperCase();
    const accountNumber = orderInfo?.account_number || '';
    const accountName = orderInfo?.account_name || 'Hokky Sumber Makmur';

    const bankBadge = getBankBadge(bankName);
    const orderDetailsUrl = transactionCode !== '-' ? `/account/orders/${transactionCode}` : '/account/orders';

    // WhatsApp Direct Message
    const waText =
        `Halo Admin Hokky Bangunan, saya ingin konfirmasi pembayaran pesanan saya:
• No. Transaksi: ${transactionCode}
• Tanggal: ${transactionDate}
• Bank Tujuan: ${bankName} (${accountNumber})
• Total Transfer: ${formatRupiah(transactionTotal)}

Berikut saya kirimkan bukti transfernya. Mohon segera diproses. Terima kasih!`;

    const whatsappUrl = `https://wa.me/6281230553236?text=${encodeURIComponent(waText)}`;

    const breadcrumb = [
        { title: 'Home', url: '/' },
        { title: 'Keranjang', url: '/shop/cart' },
        { title: 'Pesanan Berhasil', url: '' },
    ];

    return (
        <div className="order-success-page-block">
            <Helmet>
                <title>{`Pesanan Berhasil | ${theme.name}`}</title>
            </Helmet>

            <PageHeader breadcrumb={breadcrumb} />

            <div className="container hokky-cart-container">
                    <div className="order-success-card-unified">
                    {/* Header Info Section */}
                    <div className="order-header-compact">
                        <div className="order-success-icon-wrap">
                            <i className="fas fa-check" />
                        </div>
                        <h1 className="order-success-title">Pesanan Berhasil Dibuat</h1>

                        {/* Point-to-point Badges */}
                        <div className="order-info-pills">
                            <span className="order-pill">
                                No. Transaksi: <strong>#{transactionCode}</strong>
                                {transactionCode !== '-' && (
                                    <button
                                        type="button"
                                        className="btn-copy-mini"
                                        title="Salin Kode Transaksi"
                                        onClick={() => handleCopy(transactionCode, 'code', 'Kode transaksi')}
                                    >
                                        <i className={copiedField === 'code' ? 'fas fa-check text-success' : 'far fa-copy'} />
                                    </button>
                                )}
                            </span>
                            <span className="order-pill">
                                Tanggal: <strong>{transactionDate}</strong>
                            </span>
                            <span className="order-pill order-pill-status">
                                <i className="fas fa-hourglass-half mr-1" /> Menunggu Pembayaran
                            </span>
                            <span className="order-pill order-pill-deadline">
                                <i className="far fa-clock mr-1" /> Batas Waktu: 1 x 24 Jam
                            </span>
                        </div>
                    </div>

                    {/* 2-Column Landscape Body */}
                    <div className="order-body-landscape">
                        {/* LEFT COLUMN: Bank Transfer Details */}
                        <div className="order-section-left">
                            <h2 className="order-section-heading">
                                <i className="fas fa-university" /> Rekening Tujuan Transfer
                            </h2>

                            <div className="order-bank-bar">
                                <div className="bank-bar-left">
                                    <span
                                        className="bank-badge-pill"
                                        style={{ backgroundColor: bankBadge.bg, color: bankBadge.color }}
                                    >
                                        {bankBadge.label}
                                    </span>
                                    <span className="bank-bar-name">{bankName}</span>
                                </div>
                            </div>

                            {/* Nomor Rekening Row */}
                            <div className="order-data-row">
                                <div className="data-row-info">
                                    <span className="data-row-label">Nomor Rekening</span>
                                    <span className="data-row-value mono">{accountNumber || '-'}</span>
                                </div>
                                {accountNumber && (
                                    <button
                                        type="button"
                                        className={`btn-copy-chip ${copiedField === 'account' ? 'copied' : ''}`}
                                        onClick={() => handleCopy(accountNumber, 'account', 'Nomor rekening')}
                                    >
                                        {copiedField === 'account' ? (
                                            <>
                                                <i className="fas fa-check text-success mr-1" /> Tersalin
                                            </>
                                        ) : (
                                            <>
                                                <i className="far fa-copy mr-1" /> Salin Rekening
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            {/* Total Pembayaran Row */}
                            <div className="order-data-row">
                                <div className="data-row-info">
                                    <span className="data-row-label">Total yang Harus Dibayar</span>
                                    <span className="data-row-value">{formatRupiah(transactionTotal)}</span>
                                </div>
                                {transactionTotal > 0 && (
                                    <button
                                        type="button"
                                        className={`btn-copy-chip ${copiedField === 'total' ? 'copied' : ''}`}
                                        onClick={() => handleCopy(transactionTotal, 'total', 'Total pembayaran')}
                                    >
                                        {copiedField === 'total' ? (
                                            <>
                                                <i className="fas fa-check text-success mr-1" /> Tersalin
                                            </>
                                        ) : (
                                            <>
                                                <i className="far fa-copy mr-1" /> Salin Jumlah
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            <p className="order-transfer-note">
                                <i className="fas fa-info-circle" />
                                Transfer nominal sesuai persis sampai digit terakhir untuk kemudahan verifikasi.
                            </p>
                        </div>

                        {/* RIGHT COLUMN: Confirmation Points & Direct WhatsApp Button */}
                        <div className="order-section-right">
                            <div>
                                <h2 className="order-section-heading">
                                    <i className="fab fa-whatsapp text-success" /> Konfirmasi Pembayaran
                                </h2>

                                <ul className="order-points-list">
                                    <li>
                                        <span className="point-dot">1</span>
                                        <span>Lakukan transfer ke rekening bank di samping sesuai nominal tagihan.</span>
                                    </li>
                                    <li>
                                        <span className="point-dot">2</span>
                                        <span>Simpan atau tangkap layar (screenshot) bukti transfer Anda.</span>
                                    </li>
                                    <li>
                                        <span className="point-dot">3</span>
                                        <span>Kirimkan bukti transfer via WhatsApp agar pesanan langsung diproses.</span>
                                    </li>
                                </ul>

                                {/* Langsung Button Hijau */}
                                <a
                                    href={whatsappUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-whatsapp-direct"
                                >
                                    <i className="fab fa-whatsapp fa-lg mr-2" />
                                    Kirim Bukti via WhatsApp
                                </a>

                                <span className="confirm-wa-sub">
                                    Otomatis membuka chat CS resmi Hokky (0812-3055-3236)
                                </span>
                            </div>

                            {/* Navigation Actions */}
                            <div>
                                <div className="order-secondary-actions">
                                    <Link to={orderDetailsUrl} className="btn-action-primary">
                                        <i className="fas fa-receipt mr-2" /> Detail Pesanan
                                    </Link>
                                    <Link to="/" className="btn-action-outline">
                                        <i className="fas fa-shopping-bag mr-2" /> Belanja Lagi
                                    </Link>
                                </div>

                                <p className="order-support-note">
                                    Butuh bantuan lain? <a href="https://wa.me/6281230553236" target="_blank" rel="noopener noreferrer">Hubungi Customer Service</a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
